import * as React from 'react'
import { useEffect } from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { Box, Grid } from 'cs-kevlar-react'
import { SupportUserPermission } from '../../api'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  createStyles,
  makeStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import {
  DiagnosticCard,
  DiagnosticType,
} from 'components/diagnostics/DiagnosticCard'
import { EditNebulaLogLevels } from 'components/diagnostics/EditNebulaLogLevels'
import { SidePanelContent } from 'components'
import { EditImportConfiguration } from 'components/diagnostics/EditImportConfiguration'
import { EditDatabaseConfiguration } from 'components/diagnostics/EditDatabaseConfiguration'
import { EditLocationSharingConfiguration } from 'components/diagnostics/EditLocationSharingConfiguration'
import { EditBlueGreenConfiguration } from 'components/diagnostics/EditBlueGreenConfiguration'
import { EditCertificateConfiguration } from 'components/diagnostics/EditCertificateConfiguration'
import { useAuth, useDiagnosticsApi } from 'hooks'
import { hasOneOfPermissions } from 'utils'
import { EditSupportFederatedAuthenticationConfiguration } from 'components/diagnostics/EditSupportFederatedAuthenticationConfiguration'
import { usePageViewAudit } from 'hooks/usePageViewAudit'

const PageTitle = 'Diagnostics'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    contentBox: {},
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    gridContainer: {
      marginBottom: theme.spacing(8),
    },
  }),
)

const rootRoute = '/diagnostics'
const nebulaLogsRoute = '/diagnostics/logs'
const nebulaRoute = '/diagnostics/nebulaLogLevels'
const orleansDashboardRoute = '/diagnostics/orleans'
const databaseRoute = '/diagnostics/database'
const locationSharingRoute = '/diagnostics/locationSharing'
const importConfigurationRoute = '/diagnostics/importConfiguration'
const blueGreenRoute = '/diagnostics/blueGreen'
const certificateConfigurationRoute = '/diagnostics/certificateConfiguration'
const supportFederatedAuthenticationConfigurationRoute =
  '/diagnostics/supportFederatedAuthenticationConfiguration'

const openExternalUrl = (url: String) => {
  console.log(url)
  window.open(url, "_blank", "noreferrer")
}

const getDiagnosticTypeFromRoute = (route: String) => {
  if (route === nebulaRoute) return DiagnosticType.NebulaLogLevels
  if (route === databaseRoute) return DiagnosticType.Database
  if (route === locationSharingRoute) return DiagnosticType.LocationSharing
  if (route === certificateConfigurationRoute)
    return DiagnosticType.CertificateConfiguration
  if (route === supportFederatedAuthenticationConfigurationRoute)
    return DiagnosticType.SupportFederatedAuthenticationConfiguration
  if (route === blueGreenRoute) {
    return DiagnosticType.BlueGreen
  }
  return DiagnosticType.Root
}

const Diagnostics: React.FunctionComponent = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const { user: authUser } = useAuth()
  const [diagnosticType, setDiagnosticType] = React.useState<DiagnosticType>(
    getDiagnosticTypeFromRoute(location.pathname),
  )
  const {
    getImportConfiguration,
    updateImportConfiguration,
  } = useDiagnosticsApi()

  usePageViewAudit(PageTitle, true, true)

  const handleRoutes = (diagnosticType: DiagnosticType) => {
    if (diagnosticType === DiagnosticType.NebulaLogLevels)
      return navigate(nebulaRoute)
    if (diagnosticType === DiagnosticType.Database)
      return navigate(databaseRoute)
    if (diagnosticType === DiagnosticType.LocationSharing)
      return navigate(locationSharingRoute)
    if (diagnosticType === DiagnosticType.ImportConfiguration)
      return navigate(importConfigurationRoute)
    if (diagnosticType === DiagnosticType.CertificateConfiguration)
      return navigate(certificateConfigurationRoute)
    if (
      diagnosticType ===
      DiagnosticType.SupportFederatedAuthenticationConfiguration
    )
      return navigate(supportFederatedAuthenticationConfigurationRoute)
    if (diagnosticType === DiagnosticType.BlueGreen)
      return navigate(blueGreenRoute)

    if (diagnosticType === DiagnosticType.Root) return navigate(rootRoute)
  }

  React.useEffect(() => {
    handleRoutes(diagnosticType)
  }, [location.pathname, diagnosticType])

  if (!authUser?.isSupport) return null

  return (
    <React.Fragment>
      {
        <Box>
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            justifyContent="flex-start"
            className={classes.gridContainer}
          >
            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDiagnosticConfiguration,
              SupportUserPermission.ManageDiagnosticConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Logs"
                  description="View logs and dashboards."
                  editText="View"
                  type={DiagnosticType.NebulaLogs}
                  onEdit={() => openExternalUrl(nebulaLogsRoute)}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDiagnosticConfiguration,
              SupportUserPermission.ManageDiagnosticConfiguration,
            ]) && (
                <Grid item>
                  <DiagnosticCard
                      title="Orleans Dashboard"
                      description="View Orleans Dashboard."
                      editText="View"
                      type={DiagnosticType.OrleansDashboard}
                      onEdit={() => openExternalUrl(orleansDashboardRoute)}
                  />
                </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDiagnosticConfiguration,
              SupportUserPermission.ManageDiagnosticConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Nebula Log Levels"
                  description="Override Nebula log levels."
                  type={DiagnosticType.NebulaLogLevels}
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewInterfaceConfiguration,
              SupportUserPermission.ManageInterfaceConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Import Configuration"
                  description="Global import configurations."
                  type={DiagnosticType.ImportConfiguration}
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDatabaseConfiguration,
              SupportUserPermission.ManageDatabaseConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Database Configuration"
                  description="Update global database configuration."
                  type={DiagnosticType.Database}
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDiagnosticConfiguration,
              SupportUserPermission.ManageDiagnosticConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Device Location Sharing Configuration"
                  description="Update how often devices push locations to help stack performance."
                  type={DiagnosticType.LocationSharing}
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewBlueGreenConfiguration,
              SupportUserPermission.ManageBlueGreenConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Blue Green Configuration"
                  description="Update Blue Green configurations."
                  type={DiagnosticType.BlueGreen}
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDiagnosticConfiguration,
              SupportUserPermission.ManageDiagnosticConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Certificate Configuration"
                  description="Override default certificate configurations like valid duration."
                  type={DiagnosticType.CertificateConfiguration}
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}

            {hasOneOfPermissions(authUser, [
              SupportUserPermission.ViewDiagnosticConfiguration,
              SupportUserPermission.ManageDiagnosticConfiguration,
            ]) && (
              <Grid item>
                <DiagnosticCard
                  title="Support Federated Authentication Configuration"
                  description="Override default federated authentication configuration."
                  type={
                    DiagnosticType.SupportFederatedAuthenticationConfiguration
                  }
                  onEdit={setDiagnosticType}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      }
      {diagnosticType === DiagnosticType.NebulaLogLevels && (
        <SidePanelContent
          anchor="right"
          title="Nebula Log Levels"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditNebulaLogLevels
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
          />
        </SidePanelContent>
      )}
      {diagnosticType === DiagnosticType.ImportConfiguration && (
        <SidePanelContent
          anchor="right"
          title="Import Configuration"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditImportConfiguration
            customerAgencyId={null}
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
            getter={getImportConfiguration}
            updater={updateImportConfiguration}
          />
        </SidePanelContent>
      )}
      {diagnosticType === DiagnosticType.Database && (
        <SidePanelContent
          anchor="right"
          title="Database Configuration"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditDatabaseConfiguration
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
          />
        </SidePanelContent>
      )}
      {diagnosticType === DiagnosticType.LocationSharing && (
        <SidePanelContent
          anchor="right"
          title="Device Location Sharing Configuration"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditLocationSharingConfiguration
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
          />
        </SidePanelContent>
      )}
      {diagnosticType === DiagnosticType.BlueGreen && (
        <SidePanelContent
          anchor="right"
          title="Blue Green Configuration"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditBlueGreenConfiguration
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
          />
        </SidePanelContent>
      )}
      {diagnosticType === DiagnosticType.CertificateConfiguration && (
        <SidePanelContent
          anchor="right"
          title="Certificate Configuration"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditCertificateConfiguration
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
          />
        </SidePanelContent>
      )}
      {diagnosticType ===
        DiagnosticType.SupportFederatedAuthenticationConfiguration && (
        <SidePanelContent
          anchor="right"
          title="Support Federated Authentication Configuration"
          onClose={() => setDiagnosticType(DiagnosticType.Root)}
        >
          <EditSupportFederatedAuthenticationConfiguration
            onClose={() => setDiagnosticType(DiagnosticType.Root)}
          />
        </SidePanelContent>
      )}
    </React.Fragment>
  );
}

export const DiagnosticsPage: PageDefinition = {
  routes: (user) => {
    const availableRoutes = [rootRoute]
    // only register routes if the user has permission to access the route
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewDiagnosticConfiguration,
        SupportUserPermission.ManageDiagnosticConfiguration,
      ])
    )
      availableRoutes.push(nebulaRoute)
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewDatabaseConfiguration,
        SupportUserPermission.ManageDatabaseConfiguration,
      ])
    )
      availableRoutes.push(databaseRoute)
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewDiagnosticConfiguration,
        SupportUserPermission.ManageDiagnosticConfiguration,
      ])
    )
      availableRoutes.push(locationSharingRoute)
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewInterfaceConfiguration,
        SupportUserPermission.ManageInterfaceConfiguration,
      ])
    )
      availableRoutes.push(importConfigurationRoute)
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewBlueGreenConfiguration,
        SupportUserPermission.ManageBlueGreenConfiguration,
      ])
    )
      availableRoutes.push(blueGreenRoute)
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewDiagnosticConfiguration,
        SupportUserPermission.ManageDiagnosticConfiguration,
      ])
    )
      availableRoutes.push(certificateConfigurationRoute)
    if (
      hasOneOfPermissions(user, [
        SupportUserPermission.ViewDiagnosticConfiguration,
        SupportUserPermission.ManageDiagnosticConfiguration,
      ])
    )
      availableRoutes.push(supportFederatedAuthenticationConfigurationRoute)
    return availableRoutes
  },
  type: [PageType.Support],
  sortOrder: () => 4,
  component: <Diagnostics />,
  navLinkText: PageTitle,
  pageTitle: PageTitle,
  canAccess: (user) => {
    return hasOneOfPermissions(user, [
      SupportUserPermission.ViewDiagnosticConfiguration,
      SupportUserPermission.ManageDiagnosticConfiguration,
    ])
  },
  buildNavLink: (user) => {
    return rootRoute
  },
}
